
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Clock, Settings, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface TimeSettings {
  timezone: string;
  work_week: {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  };
  overtime_threshold_daily: number;
  overtime_threshold_weekly: number;
  work_hours_start: string;
  work_hours_end: string;
  break_policies: {
    lunch_break_minutes: number;
    short_break_minutes: number;
    max_breaks_per_day: number;
  };
}

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona Time (MST)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKST)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
];

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

export const TimeManagementSettings = () => {
  const [settings, setSettings] = useState<TimeSettings>({
    timezone: 'America/New_York',
    work_week: {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
    },
    overtime_threshold_daily: 8,
    overtime_threshold_weekly: 40,
    work_hours_start: '09:00',
    work_hours_end: '17:00',
    break_policies: {
      lunch_break_minutes: 60,
      short_break_minutes: 15,
      max_breaks_per_day: 3,
    },
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const settingKeys = [
        'timezone',
        'work_week',
        'overtime_threshold_daily',
        'overtime_threshold_weekly',
        'work_hours_start',
        'work_hours_end',
        'break_policies'
      ];

      const promises = settingKeys.map(key =>
        supabase.rpc('get_system_setting', { setting_name: key })
      );

      const results = await Promise.all(promises);
      
      const loadedSettings = { ...settings };
      
      results.forEach((result, index) => {
        if (result.data && result.data !== null) {
          const key = settingKeys[index];
          
          // Handle different setting types with proper casting
          switch (key) {
            case 'timezone':
            case 'work_hours_start':
            case 'work_hours_end':
              if (typeof result.data === 'string') {
                (loadedSettings as any)[key] = result.data;
              }
              break;
            case 'work_week':
            case 'break_policies':
              if (typeof result.data === 'object' && result.data !== null) {
                (loadedSettings as any)[key] = result.data;
              }
              break;
            case 'overtime_threshold_daily':
            case 'overtime_threshold_weekly':
              if (typeof result.data === 'number') {
                (loadedSettings as any)[key] = result.data;
              }
              break;
          }
        }
      });

      setSettings(loadedSettings);
    } catch (error) {
      console.error('Error loading time settings:', error);
      toast({
        title: "Error Loading Settings",
        description: "Failed to load time management settings.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      const updates = [
        { key: 'timezone', value: settings.timezone },
        { key: 'work_week', value: settings.work_week },
        { key: 'overtime_threshold_daily', value: settings.overtime_threshold_daily },
        { key: 'overtime_threshold_weekly', value: settings.overtime_threshold_weekly },
        { key: 'work_hours_start', value: settings.work_hours_start },
        { key: 'work_hours_end', value: settings.work_hours_end },
        { key: 'break_policies', value: settings.break_policies },
      ];

      const promises = updates.map(({ key, value }) =>
        supabase.rpc('update_system_setting', { 
          setting_name: key, 
          setting_val: value 
        })
      );

      await Promise.all(promises);

      toast({
        title: "Settings Saved",
        description: "Time management settings have been updated successfully.",
      });
    } catch (error) {
      console.error('Error saving time settings:', error);
      toast({
        title: "Error Saving Settings",
        description: "Failed to save time management settings.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleWorkWeekChange = (day: string, checked: boolean) => {
    setSettings(prev => ({
      ...prev,
      work_week: {
        ...prev.work_week,
        [day]: checked
      }
    }));
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Time Management Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading settings...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Time Management Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Timezone Selection */}
        <div>
          <Label htmlFor="timezone">System Timezone</Label>
          <Select value={settings.timezone} onValueChange={(value) => setSettings(prev => ({ ...prev, timezone: value }))}>
            <SelectTrigger>
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONE_OPTIONS.map(tz => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Work Week Configuration */}
        <div>
          <Label className="text-base font-medium">Work Week Configuration</Label>
          <div className="grid grid-cols-3 gap-4 mt-2">
            {DAYS_OF_WEEK.map(day => (
              <div key={day.key} className="flex items-center space-x-2">
                <Checkbox
                  id={day.key}
                  checked={settings.work_week[day.key as keyof typeof settings.work_week]}
                  onCheckedChange={(checked) => handleWorkWeekChange(day.key, checked as boolean)}
                />
                <Label htmlFor={day.key} className="text-sm">{day.label}</Label>
              </div>
            ))}
          </div>
        </div>

        {/* Work Hours */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="work_hours_start">Work Day Start</Label>
            <Input
              id="work_hours_start"
              type="time"
              value={settings.work_hours_start}
              onChange={(e) => setSettings(prev => ({ ...prev, work_hours_start: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="work_hours_end">Work Day End</Label>
            <Input
              id="work_hours_end"
              type="time"
              value={settings.work_hours_end}
              onChange={(e) => setSettings(prev => ({ ...prev, work_hours_end: e.target.value }))}
            />
          </div>
        </div>

        {/* Overtime Thresholds */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="overtime_daily">Daily Overtime Threshold (hours)</Label>
            <Input
              id="overtime_daily"
              type="number"
              min="1"
              max="24"
              value={settings.overtime_threshold_daily}
              onChange={(e) => setSettings(prev => ({ ...prev, overtime_threshold_daily: parseInt(e.target.value) || 8 }))}
            />
          </div>
          <div>
            <Label htmlFor="overtime_weekly">Weekly Overtime Threshold (hours)</Label>
            <Input
              id="overtime_weekly"
              type="number"
              min="1"
              max="168"
              value={settings.overtime_threshold_weekly}
              onChange={(e) => setSettings(prev => ({ ...prev, overtime_threshold_weekly: parseInt(e.target.value) || 40 }))}
            />
          </div>
        </div>

        {/* Break Policies */}
        <div>
          <Label className="text-base font-medium">Break Policies</Label>
          <div className="grid grid-cols-3 gap-4 mt-2">
            <div>
              <Label htmlFor="lunch_break">Lunch Break (minutes)</Label>
              <Input
                id="lunch_break"
                type="number"
                min="0"
                max="180"
                value={settings.break_policies.lunch_break_minutes}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  break_policies: {
                    ...prev.break_policies,
                    lunch_break_minutes: parseInt(e.target.value) || 0
                  }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="short_break">Short Break (minutes)</Label>
              <Input
                id="short_break"
                type="number"
                min="0"
                max="60"
                value={settings.break_policies.short_break_minutes}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  break_policies: {
                    ...prev.break_policies,
                    short_break_minutes: parseInt(e.target.value) || 0
                  }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="max_breaks">Max Breaks/Day</Label>
              <Input
                id="max_breaks"
                type="number"
                min="0"
                max="10"
                value={settings.break_policies.max_breaks_per_day}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  break_policies: {
                    ...prev.break_policies,
                    max_breaks_per_day: parseInt(e.target.value) || 0
                  }
                }))}
              />
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-800">Server-Side Timing</h4>
              <p className="text-sm text-blue-700 mt-1">
                All clock-in/out times are now managed by the server using the configured timezone. 
                This prevents time manipulation and ensures consistency across all devices.
              </p>
            </div>
          </div>
        </div>

        <Button onClick={saveSettings} disabled={isSaving} className="w-full">
          {isSaving ? 'Saving...' : 'Save Time Settings'}
        </Button>
      </CardContent>
    </Card>
  );
};
